import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { EMPLOYMENT_STATUSES } from "./utils";
import { KYC_SCORE } from "../../../LegalForm/steps/step5/utils";

import {
  CheckBoxWrapperWithDarkBAckground,
  CommonLabel,
  ContentWrapper,
  ErrorAndInputWrapper,
  GeneralInput,
  InputAndLabelWrapper,
  InputRowWrapper,
  SingleColumnWrapper,
} from "../styles";
import TextInputWithLanguageControl from "../inputs/TextInputWithLanguageControl";
import Address from "./subForms/Address";
import CustomSelect from "../inputs/CustomSelect";
import CustomSelectWithCheckbox from "../inputs/CustomSelectWithCheckbox";
import RadioQuestion from "./subForms/RadioQuestion";
import PepAndKyc from "./PepAndKyc";

export function AddressDetails({
  fullCountries,
  cities,
  transFormedCityOrVillage,
  juridicalAddressDisabled = true,
}) {
  const { t } = useTranslation();
  const { register, unregister } = useFormContext();
  const isActualAddressDifferent = useWatch({
    name: "isActualAddressDifferent",
  });

  function handleIsActualAddressDiffChange(e) {
    register("isActualAddressDifferent").onChange(e);
    if (!e.target.checked) {
      unregister("actualAddress");
    }
  }

  return (
    <>
      <InputAndLabelWrapper>
        <TextInputWithLanguageControl
          name="legalAddress"
          label={t("legalAddressFilled")}
          rules={{ required: t("required") }}
          isDark={juridicalAddressDisabled}
          disabled={juridicalAddressDisabled}
        />
      </InputAndLabelWrapper>
      <ErrorAndInputWrapper>
        <CheckBoxWrapperWithDarkBAckground>
          <GeneralInput
            type="checkbox"
            id="isActualAddressDifferent"
            {...register("isActualAddressDifferent")}
            onChange={handleIsActualAddressDiffChange}
          />
          <CommonLabel htmlFor="isActualAddressDifferent">
            {t("isActualAddress")}
          </CommonLabel>
        </CheckBoxWrapperWithDarkBAckground>
      </ErrorAndInputWrapper>
      {isActualAddressDifferent ? (
        <InputAndLabelWrapper>
          <CommonLabel>{t("actualAddress")}</CommonLabel>
          <Address
            countries={fullCountries}
            cities={cities}
            transFormedCityOrVillage={transFormedCityOrVillage}
            namePrefix="actualAddress"
          />
        </InputAndLabelWrapper>
      ) : null}
    </>
  );
}

function PersonalAndFatca(props) {
  const {
    employMentStatuses,
    moneySources,
    monthlyTurnovers,
    incomes,
    cities,
    fullCountries,
    transFormedCityOrVillage,
    activitySubjects,
    economicActivityTypes,
    isPrivateEnt,
    isEntNeutral,
    isPrivateInd,
    moneySourcesOptions,
    assets,
    propertySources,
    isPep,
    isCountryOfHighRiskChosen,
    hasKycScore,
    juridicalAddressDisabled = true,
  } = props;

  const { t } = useTranslation();
  const { register, unregister, setValue } = useFormContext();

  const isEmploymentStatusDisabled = isPrivateEnt || isEntNeutral;

  const economicActivityTypesValue = useWatch({
    name: "economicActivityTypes",
  });
  const employmentStatus = useWatch({ name: "employmentStatus" });
  const mainEconomicActivityValue = useWatch({ name: "mainEconomicActivity" });

  const employmentStatusDefaultValue = getEmploymentDefaultVal();
  const showWorkplace =
    checkTosShowWorkplace(employmentStatus) || employmentStatusDefaultValue;

  function handleEmploymentStatusChange(option) {
    if (!checkTosShowWorkplace(option)) {
      unregister(["workplace", "position"]);
    }
  }

  function checkTosShowWorkplace(option) {
    const val = option?.value;
    const {
      employed,
      selfEmployed,
      businessOwner,
      indEnterpr,
      soleProprietor,
    } = EMPLOYMENT_STATUSES;
    if (
      val === employed ||
      val === selfEmployed ||
      val === businessOwner ||
      val === indEnterpr ||
      val === soleProprietor
    ) {
      return true;
    }
    return false;
  }

  function getEmploymentDefaultVal() {
    let temp = "";
    if (isPrivateEnt) {
      temp = employMentStatuses?.find(
        (item) => item?.value === EMPLOYMENT_STATUSES.soleProprietor,
      );
    }
    if (isEntNeutral) {
      temp = employMentStatuses?.find(
        (item) => item?.value === EMPLOYMENT_STATUSES.indEnterpr,
      );
    }
    return temp;
  }

  function getEmploymentStatusForDisplay() {
    if (isPrivateInd) {
      return employMentStatuses?.filter((item) => {
        return (
          item?.value !== EMPLOYMENT_STATUSES.soleProprietor &&
          item?.value !== EMPLOYMENT_STATUSES.indEnterpr
        );
      });
    }
    return employMentStatuses;
  }

  function handleEconomicActivityTypesChange(option, item) {
    if (item) {
      if (item?.value === mainEconomicActivityValue) {
        setValue("mainEconomicActivity", null);
      }
    }
    const hasKycScore = option?.some((item) => item?.kycScore === KYC_SCORE);
    if (!hasKycScore && !isPep && !isCountryOfHighRiskChosen) {
      resetKYC();
    }
  }

  function resetKYC() {
    setValue("kyc.agreement", null);
    setValue("kyc.assets", null);
    setValue("kyc.estimatedCash", null);
    setValue("kyc.moneySourcesOptions", null);
    setValue("kyc.monthlyTurnovers", null);
    setValue("kyc.otherAssets", null);
    setValue("kyc.otherMoneySources", null);
    setValue("kyc.otherPropertySources", null);
    setValue("kyc.propertySources", null);
    setValue("kyc.infoCounterparties", null);
  }

  return (
    <ContentWrapper>
      <AddressDetails
        fullCountries={fullCountries}
        cities={cities}
        transFormedCityOrVillage={transFormedCityOrVillage}
        juridicalAddressDisabled={juridicalAddressDisabled}
      />
      <InputRowWrapper>
        <InputAndLabelWrapper>
          <CommonLabel>{t("employment_status")}</CommonLabel>
          <CustomSelect
            name="employmentStatus"
            options={getEmploymentStatusForDisplay()}
            defaultValue={employmentStatusDefaultValue}
            isDisabled={isEmploymentStatusDisabled}
            rules={{ required: t("required") }}
            handleChange={handleEmploymentStatusChange}
          />
        </InputAndLabelWrapper>
      </InputRowWrapper>
      {showWorkplace ? (
        <SingleColumnWrapper>
          <InputAndLabelWrapper>
            <TextInputWithLanguageControl
              name="workplace"
              label={t("workspace")}
              rules={{ required: t("required") }}
            />
          </InputAndLabelWrapper>
          <InputAndLabelWrapper>
            <TextInputWithLanguageControl
              name="position"
              label={t("position")}
              rules={{ required: t("required") }}
            />
          </InputAndLabelWrapper>
        </SingleColumnWrapper>
      ) : null}
      {isPrivateEnt ? (
        <InputRowWrapper>
          <InputAndLabelWrapper>
            <CommonLabel>{t("activity_subject")}</CommonLabel>
            <CustomSelect
              name="activitySubjects"
              options={activitySubjects}
              rules={{ required: t("required") }}
            />
          </InputAndLabelWrapper>
        </InputRowWrapper>
      ) : null}
      {isEntNeutral ? (
        <>
          <InputRowWrapper>
            <InputAndLabelWrapper>
              <CommonLabel>{t("legal_economic_activity")}</CommonLabel>
              <CustomSelectWithCheckbox
                name="economicActivityTypes"
                options={economicActivityTypes}
                rules={{ required: t("required") }}
                handleChange={handleEconomicActivityTypesChange}
              />
            </InputAndLabelWrapper>
          </InputRowWrapper>
          {economicActivityTypesValue?.length ? (
            <InputRowWrapper>
              <RadioQuestion
                label={t("main_economic_activity")}
                name="mainEconomicActivity"
                data={economicActivityTypesValue}
                register={register}
              />
            </InputRowWrapper>
          ) : null}
          <InputRowWrapper>
            <InputAndLabelWrapper>
              <TextInputWithLanguageControl
                name="additionalInformationAboutEconActivity"
                label={t("additional_information")}
              />
            </InputAndLabelWrapper>
          </InputRowWrapper>
        </>
      ) : null}
      <SingleColumnWrapper>
        <InputAndLabelWrapper>
          <CommonLabel>{t("income_source")}</CommonLabel>
          <CustomSelect
            name="moneySources"
            options={moneySources}
            rules={{ required: t("required") }}
          />
        </InputAndLabelWrapper>
        <InputAndLabelWrapper>
          <CommonLabel>{t("monthly_income")}</CommonLabel>
          <CustomSelect
            name="monthlyIncome"
            options={incomes}
            rules={{ required: t("required") }}
          />
        </InputAndLabelWrapper>
      </SingleColumnWrapper>
      <InputRowWrapper>
        <InputAndLabelWrapper>
          <CommonLabel>{t("exp_monthly_turnovers_in_gel")}</CommonLabel>
          <CustomSelect
            name="turnovers"
            options={monthlyTurnovers}
            rules={{ required: t("required") }}
          />
        </InputAndLabelWrapper>
      </InputRowWrapper>
      <PepAndKyc
        moneySourcesOptions={moneySourcesOptions}
        monthlyTurnovers={monthlyTurnovers}
        assets={assets}
        propertySources={propertySources}
        showCounterPartiesQuestion={isEntNeutral || isPrivateEnt}
        showKyc={isPep || isCountryOfHighRiskChosen || hasKycScore}
      />
    </ContentWrapper>
  );
}

export default PersonalAndFatca;
